import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, FileText, Calculator, TrendingUp, Clock, Users, 
  Map, Download, Search, Archive, BarChart3,
  Receipt, History, FileCheck, CheckCircle, Camera, Star
} from 'lucide-react';

const FeaturesPage = () => {
  const featureCategories = [
    {
      title: "Professional Invoicing",
      description: "Create professional invoices for the practices where you work",
      icon: <FileText className="w-8 h-8" />,
      features: [
        {
          icon: <FileText className="w-6 h-6" />,
          title: "Unlimited Professional Invoices",
          description: "Create unlimited invoices with your personal branding and bank details pre-filled",
          plan: "Basic"
        },
        {
          icon: <Download className="w-6 h-6" />,
          title: "PDF Generation",
          description: "Generate professional PDF invoices that you can download and send to practices",
          plan: "Basic"
        },
        {
          icon: <Users className="w-6 h-6" />,
          title: "Multi-Practice Management",
          description: "Manage invoices for multiple dental practices from one central dashboard",
          plan: "Basic"
        },
        {
          icon: <Clock className="w-6 h-6" />,
          title: "Monthly & Daily Invoice Options",
          description: "Choose between monthly summary invoices or detailed daily work logs",
          plan: "Pro"
        },
        {
          icon: <History className="w-6 h-6" />,
          title: "Invoice History & Payment Tracking",
          description: "Complete searchable history of all invoices with payment status tracking",
          plan: "Pro"
        }
      ]
    },
    {
      title: "Expense Management",
      description: "Track your professional expenses to maximize tax deductions",
      icon: <Receipt className="w-8 h-8" />,
      features: [
        {
          icon: <Camera className="w-6 h-6" />,
          title: "Smart Camera Receipt Scanning",
          description: "Use your phone camera to automatically scan and categorize receipts",
          plan: "Pro"
        },
        {
          icon: <Receipt className="w-6 h-6" />,
          title: "Expense Categorization",
          description: "Automatic categorization of expenses for easier tax preparation",
          plan: "Pro"
        },
        {
          icon: <Map className="w-6 h-6" />,
          title: "Mileage Tracking",
          description: "Track travel between practices and calculate mileage deductions",
          plan: "Pro"
        },
        {
          icon: <Archive className="w-6 h-6" />,
          title: "Expense History & Reports",
          description: "View all expenses with filtering and export capabilities",
          plan: "Pro"
        }
      ]
    },
    {
      title: "Financial Analytics & Tax Tools",
      description: "Understand your earnings and prepare for tax time",
      icon: <TrendingUp className="w-8 h-8" />,
      features: [
        {
          icon: <BarChart3 className="w-6 h-6" />,
          title: "Revenue Analytics",
          description: "Visual charts showing income trends and earnings by practice",
          plan: "Pro"
        },
        {
          icon: <Calculator className="w-6 h-6" />,
          title: "Tax Estimation Calculator",
          description: "Real-time estimates of tax liability based on income and expenses",
          plan: "Pro"
        },
        {
          icon: <Clock className="w-6 h-6" />,
          title: "Daily Work Log",
          description: "Track daily activities, hours worked, and earnings for complete records",
          plan: "Pro"
        },
        {
          icon: <FileCheck className="w-6 h-6" />,
          title: "Tax-Ready Reports",
          description: "Generate summary reports suitable for your accountant",
          plan: "Pro"
        }
      ]
    },
    {
      title: "Job Search & Opportunities",
      description: "Find dental job opportunities in your area",
      icon: <Search className="w-8 h-8" />,
      features: [
        {
          icon: <Map className="w-6 h-6" />,
          title: "Location-Based Job Search",
          description: "Find dental opportunities within your preferred travel distance",
          plan: "Basic"
        }
      ]
    }
  ];

  const testimonialFeatures = [
    {
      quote: "The mileage tracking alone saves me hundreds in tax deductions I was missing before.",
      author: "Sarah M., Dental Hygienist",
      feature: "Expense Management"
    },
    {
      quote: "Managing invoices for 5 different practices used to be chaos. Now it's completely organized.",
      author: "James R., Locum Dentist",
      feature: "Multi-Practice Management"
    },
    {
      quote: "The tax estimation feature helps me set aside the right amount each month. No more surprises!",
      author: "Lisa K., Dental Therapist",
      feature: "Tax Planning"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Features - Complete Dental Professional Management Software | Coooly</title>
        <meta 
          name="description" 
          content="Comprehensive features for dental professionals: invoice management, expense tracking, tax estimation, job search, and more. Built for hygienists, therapists, nurses, and locums." 
        />
        <meta 
          name="keywords" 
          content="dental professional software features, hygienist invoice management, dental expense tracking, locum dental tools, dental therapist billing, dental practice software" 
        />
        <link rel="canonical" href={`${window.location.origin}/features`} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Features - Complete Dental Professional Management Software" />
        <meta property="og:description" content="Comprehensive management tools for dental professionals including invoicing, expense tracking, and tax estimation." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/features`} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Coooly Dental Professional Software",
            "description": "Complete management software for dental professionals including invoice management, expense tracking, and tax estimation",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "25",
              "priceCurrency": "GBP",
              "billingIncrement": "P1Y"
            },
            "featureList": [
              "Invoice Management",
              "Expense Tracking", 
              "Tax Estimation",
              "Multi-Practice Management",
              "Job Search",
              "Financial Analytics"
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-blue-600 text-white">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-between mb-8">
              <Link 
                to="/"
                className="flex items-center gap-2 text-white hover:text-purple-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Home
              </Link>
              <div className="flex gap-4">
                <Link to="/blog" className="text-white hover:text-purple-100 transition-colors">
                  Blog
                </Link>
                <Link to="/pricing" className="text-white hover:text-purple-100 transition-colors">
                  Pricing
                </Link>
              </div>
            </div>
            
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent">
                Complete Features for Dental Professionals
              </h1>
              <p className="text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto leading-relaxed">
                Everything you need to manage your dental professional business efficiently - from invoicing and expense tracking 
                to tax planning and job opportunities.
              </p>
            </div>
          </div>
        </div>

        {/* Feature Categories */}
        <div className="container mx-auto px-4 py-16">
          <div className="space-y-16">
            {featureCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="max-w-6xl mx-auto">
                {/* Category Header */}
                <div className="text-center mb-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-2xl mb-4">
                    {category.icon}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {category.title}
                  </h2>
                  <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    {category.description}
                  </p>
                </div>

                {/* Feature Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.features.map((feature, featureIndex) => (
                    <Card key={featureIndex} className="group hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-purple-300 bg-white">
                      <CardHeader className="pb-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="p-3 bg-purple-100 rounded-xl group-hover:bg-purple-200 transition-colors">
                            <div className="text-purple-600">
                              {feature.icon}
                            </div>
                          </div>
                          <Badge 
                            variant={feature.plan === 'Pro' ? 'default' : 'secondary'}
                            className={feature.plan === 'Pro' ? 'bg-purple-600' : 'bg-gray-500'}
                          >
                            {feature.plan}
                          </Badge>
                        </div>
                        <CardTitle className="text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                          {feature.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-gray-700 leading-relaxed">
                          {feature.description}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="bg-white py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Loved by Dental Professionals
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                See how our features are helping dental professionals streamline their practice management.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {testimonialFeatures.map((testimonial, index) => (
                <Card key={index} className="text-center bg-white border border-gray-200 shadow-md">
                  <CardContent className="pt-8">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-6 h-6 text-purple-600 fill-current" />
                    </div>
                    <blockquote className="text-lg text-gray-700 mb-4 italic">
                      "{testimonial.quote}"
                    </blockquote>
                    <p className="font-semibold text-gray-900">
                      {testimonial.author}
                    </p>
                    <Badge className="mt-2 bg-purple-100 text-purple-800">
                      {testimonial.feature}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Perfect Plan
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Both plans include a 60-day free trial. Start with Basic and upgrade anytime.
            </p>
          </div>

          <div className="flex justify-center">
            <div className="flex flex-col lg:flex-row gap-8 items-stretch max-w-5xl w-full">
              <Card className="flex flex-col border border-gray-200 bg-white shadow-md flex-1 min-w-[400px]">
                <CardHeader className="text-center pb-4 flex-grow">
                  <CardTitle className="text-2xl font-bold text-gray-900">Basic Plan</CardTitle>
                  <p className="text-gray-700">Perfect for getting started</p>
                  <div className="text-3xl font-bold text-gray-900 mt-4">£25<span className="text-lg font-normal">/year</span></div>
                </CardHeader>
                <CardContent className="flex flex-col flex-grow justify-between">
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-900">Unlimited invoices & PDF generation</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-900">Multi-practice management</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-900">Secure data storage & backup</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-900">Job search & practice directory</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-900">Email support</span>
                    </li>
                  </ul>
                  <div className="flex-grow flex flex-col justify-end">
                    <Button asChild className="w-full mt-6 bg-purple-600 hover:bg-purple-700">
                      <Link to="/pricing">Start Basic Trial</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="flex flex-col border-2 border-purple-500 relative bg-white shadow-md flex-1 min-w-[400px]">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </div>
                <CardHeader className="text-center pb-4 flex-grow">
                  <CardTitle className="text-2xl font-bold text-gray-900">Pro Plan</CardTitle>
                  <p className="text-gray-700">Complete professional solution</p>
                  <div className="text-3xl font-bold text-gray-900 mt-4">£50<span className="text-lg font-normal">/year</span></div>
                </CardHeader>
                <CardContent className="flex flex-col flex-grow justify-between">
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-900">Everything in Basic</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-900">Expense tracking with Smart Camera</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-900">Mileage tracking between practices</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-900">Tax estimation & analytics</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-900">Revenue reports & daily work log</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-900">Data export & email support</span>
                    </li>
                  </ul>
                  <div className="flex-grow flex flex-col justify-end">
                    <Button asChild className="w-full mt-6 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
                      <Link to="/pricing">Start Pro Trial</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-blue-600 text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Streamline Your Dental Professional Business?
            </h2>
            <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Join thousands of dental professionals who have simplified their invoicing, expense tracking, and financial management.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                className="px-8 py-4 bg-white text-purple-600 hover:bg-gray-100 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                <Link to="/pricing">
                  View Pricing Plans
                </Link>
              </Button>
              <Button 
                asChild
                className="px-8 py-4 bg-transparent border-2 border-white text-white hover:bg-white hover:text-purple-600 text-lg font-semibold transition-all transform hover:scale-105"
              >
                <Link to="/">
                  Start Free Trial
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FeaturesPage;
